import React from 'react';
import { Instagram, Facebook, Linkedin, Youtube, Phone } from 'lucide-react';
import { trackSocialClick } from '@/lib/ga4Tracking.js';
import { handleWhatsAppClick } from '@/lib/whatsapp.js';
import WhatsAppIcon from './WhatsAppIcon.jsx';
import logoHiperpan from '@/assets/logo-hiperpan.png';

const Footer = () => {
  const socialLinks = [
    { 
      name: 'Instagram', 
      icon: Instagram, 
      url: 'https://www.instagram.com/hiperpancongelados/',
      ariaLabel: 'Visite nosso Instagram',
      hoverColor: 'hover:text-pink-500'
    },
    { 
      name: 'Facebook', 
      icon: Facebook, 
      url: 'https://www.facebook.com/hiperpancongelados/',
      ariaLabel: 'Visite nosso Facebook',
      hoverColor: 'hover:text-blue-600'
    },
    { 
      name: 'LinkedIn', 
      icon: Linkedin, 
      url: 'https://www.linkedin.com/company/hiperpan-ind-e-comercio-alimentos-congelados-ltda/',
      ariaLabel: 'Visite nosso LinkedIn',
      hoverColor: 'hover:text-blue-700'
    },
    { 
      name: 'YouTube', 
      icon: Youtube, 
      url: 'https://www.youtube.com/@hiperpan764',
      ariaLabel: 'Visite nosso YouTube',
      hoverColor: 'hover:text-red-500'
    }
  ];

  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 md:pt-20 pb-12 w-full max-w-full overflow-hidden border-t border-emerald-900/30">
      <div className="container-custom">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 pb-12 md:pb-16 border-b border-emerald-900/30 w-full">
          
          <div className="flex flex-col max-w-lg w-full">
            <a href="/" className="inline-block mb-6 md:mb-8 hover:opacity-80 transition-opacity duration-300">
              <img
                src={logoHiperpan}
                alt="Logotipo oficial da Hiperpan"
                width="320"
                height="200"
                loading="lazy"
                className="w-[140px] sm:w-[180px] h-auto object-contain"
              />
            </a>
            <p className="text-slate-400 font-medium leading-relaxed mb-6 md:mb-8 text-base md:text-lg break-words w-full">
              Há 35 anos buscando fornecer soluções consistentes em panificação para o Rio Grande do Sul.
            </p>
            
            <address className="text-sm text-slate-500 font-medium not-italic mb-8 w-full break-words" itemScope itemType="https://schema.org/LocalBusiness">
              <p itemProp="name" className="text-slate-400 font-bold mb-2">Hiperpan Indústria e Comércio de Alimentos Congelados Ltda.</p>
              <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="space-y-1">
                <p><span itemProp="streetAddress">R. Acilino Francisco de Medeiros, 600 - Distrito Industrial</span></p>
                <p><span itemProp="addressLocality">Gravataí</span> - <span itemProp="addressRegion">RS</span></p>
                <p>CEP: <span itemProp="postalCode">94045-410</span>, <span itemProp="addressCountry">Brasil</span></p>
              </div>
              <meta itemProp="telephone" content="+5551995420336" />
            </address>
            
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-2 w-full">
              {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return (
                  <a 
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick(social.name, 'footer')}
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg p-2 sm:p-3 bg-slate-900 border border-emerald-900/50 flex items-center justify-center text-slate-400 transition-all duration-300 hover:bg-gray-100 ${social.hoverColor} flex-shrink-0`}
                    aria-label={social.ariaLabel}
                  >
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </a>
                );
              })}
            </div>
          </div>
          
          <div className="relative group overflow-hidden bg-slate-900 p-6 sm:p-8 rounded-2xl md:rounded-3xl border border-emerald-800/50 hover:border-emerald-500/50 transition-all duration-300 flex flex-col justify-center shadow-lg w-full">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none blur-2xl"></div>

            <div className="relative z-10 flex flex-col items-start w-full">
              <div className="inline-flex w-12 h-12 bg-emerald-500/20 rounded-xl items-center justify-center mb-6 flex-shrink-0">
                <Phone className="w-6 h-6 text-emerald-400" />
              </div>
              
              <h3 className="text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 break-words w-full">
                Atendimento Comercial Direto
              </h3>
              
              <a 
                href="tel:51995420336" 
                aria-label="Ligar para (51) 99542-0336"
                className="block text-3xl sm:text-4xl lg:text-5xl font-black text-white hover:text-green-700 transition-colors duration-300 mb-2 tracking-tight break-words max-w-full"
              >
                (51) 99542-0336
              </a>
              <span className="text-xs text-emerald-500/70 mb-6 sm:mb-8 block break-words w-full">Clique para ligar do seu smartphone</span>
              
              <button 
                data-gtm="whatsapp"
                onClick={() => handleWhatsAppClick('Olá! Gostaria de falar com um consultor da Hiperpan.')}
                className="gtm-whatsapp footer-cta-button group w-full flex py-3 sm:py-4 px-4"
              >
                <div className="footer-cta-button-icon flex-shrink-0">
                  <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="footer-cta-button-text flex-1 whitespace-normal break-words leading-tight px-2 text-sm sm:text-base">
                  Falar com Consultor Agora
                </div>
              </button>
            </div>
          </div>
          
        </div>

        <div className="pt-8 md:pt-10 pb-4 text-center text-xs sm:text-sm text-slate-500 font-medium w-full">
          <p className="break-words text-center">© {new Date().getFullYear()} Hiperpan. Todos os direitos reservados.</p>
        </div>
        
      </div>
    </footer>
  );
};

export default Footer;